<template>
  <el-dialog
    title="选择任务"
    :visible.sync="dialogVisible"
    width="700px"
    append-to-body
    class="task-setting-dialog"
    @close="onClose"
  >
    <div class="dialog-body">
      <div class="search-row">
        <span class="label">任务名称</span>
        <el-input
          v-model="searchKeyword"
          placeholder="请输入"
          size="small"
          class="search-input"
          clearable
          @keyup.enter.native="doSearch"
        />
        <el-button type="primary" size="small" @click="doSearch">搜索</el-button>
      </div>
      <div class="selected-row">
        <span class="label">已选择:</span>
        <div class="selected-tags">
          <el-tag
            v-for="(item, index) in innerSelected"
            :key="tagKey(item, index)"
            closable
            size="small"
            class="selected-tag"
            @close="removeSelected(index)"
          >
            {{ item.task_name }}
          </el-tag>
          <span v-if="!innerSelected.length" class="placeholder">暂无已选任务</span>
        </div>
      </div>
      <el-table
        ref="taskTable"
        :data="loadedTaskList"
        border
        size="small"
        class="task-table"
        @selection-change="onSelectionChange"
      >
        <el-table-column type="selection" width="48" align="center" />
        <el-table-column type="index" label="序号" width="60" align="center" :index="indexFromOne" />
        <el-table-column prop="task_name" label="任务名称" min-width="140" />
        <el-table-column prop="count" label="人数" width="80" align="center" />
      </el-table>
      <div class="pagination-row">
        <el-pagination
          :current-page="pageNum"
          :page-size="pageSize"
          :total="total"
          layout="total, prev, pager, next"
          small
          @current-change="handlePageChange"
        />
      </div>
    </div>
    <span slot="footer" class="dialog-footer">
      <el-button size="small" @click="dialogVisible = false">取消</el-button>
      <el-button size="small" type="primary" @click="handleConfirm">确认</el-button>
    </span>
  </el-dialog>
</template>

<script>
import schedulingApi from "@/api/scheduling";

export default {
  name: "TaskSettingDialog",
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    value: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      searchKeyword: "",
      innerSelected: [],
      loadedTaskList: [],
      pageNum: 1,
      pageSize: 10,
      total: 0,
      syncingTable: false, // 程序内勾选表格时忽略 selection-change，避免覆盖 innerSelected
    };
  },
  computed: {
    dialogVisible: {
      get() {
        return this.visible;
      },
      set(val) {
        this.$emit("update:visible", val);
      },
    },
  },
  watch: {
    visible(val) {
      if (val) {
        this.innerSelected = this.normalizeTaskList(this.value || []);
        this.searchKeyword = "";
        this.pageNum = 1;
        this.loadTaskList();
      } else {
        this.innerSelected = [];
        this.loadedTaskList = [];
        this.total = 0;
      }
    },
  },
  methods: {
    // 统一任务项结构，支持父组件传 id/task_id、task_name/title、count/member_count 等
    normalizeTaskList(list) {
      return (list || []).map((t) => ({
        task_id: t.task_id != null ? t.task_id : t.id,
        id: t.id != null ? t.id : t.task_id,
        task_name: t.task_name != null ? t.task_name : (t.title || ""),
        count: t.count != null ? t.count : (t.member_count != null ? t.member_count : 0),
        work_location: t.work_location != null ? t.work_location : (t.workplace || ""),
        punch_range_m: t.punch_range_m != null ? t.punch_range_m : 500,
      }));
    },
    async loadTaskList() {
      try {
        const res = await schedulingApi.getAttendanceGroupTaskOptions({
          task_name: this.searchKeyword ? this.searchKeyword.trim() || undefined : undefined,
          current_page: this.pageNum,
          page_size: this.pageSize,
        });
        const list = (res && (res.data || res.list)) || [];
        const raw = Array.isArray(list) ? list : [];
        this.loadedTaskList = raw.map((item) => ({
          id: item.id,
          task_id: item.id,
          task_name: item.task_name || item.name || "",
          count: item.member_count != null ? item.member_count : (item.count != null ? item.count : 0),
          work_location: item.workplace != null ? item.workplace : (item.work_location || ""),
          punch_range_m: item.punch_range_m != null ? item.punch_range_m : 500,
        }));
        const pages = res && res.pages;
        this.total = (pages && pages.total) || 0;
      } catch (e) {
        this.loadedTaskList = [];
        this.total = 0;
      } finally {
        this.$nextTick(() => this.syncTableSelection());
      }
    },
    indexFromOne(index) {
      return (this.pageNum - 1) * this.pageSize + index + 1;
    },
    handlePageChange(val) {
      this.pageNum = val;
      this.loadTaskList();
    },
    tagKey(item, index) {
      return (item.task_id != null ? String(item.task_id) : item.task_name) + `_${item.count}_${index}`;
    },
    doSearch() {
      this.pageNum = 1;
      this.loadTaskList();
    },
    removeSelected(index) {
      this.innerSelected.splice(index, 1);
      this.$nextTick(() => this.syncTableSelection());
    },
    onSelectionChange(rows) {
      if (this.syncingTable) return;
      const currentRows = this.loadedTaskList || [];
      if (!currentRows.length) return;
      const isOnCurrentPage = (sel) =>
        currentRows.some(
          (r) =>
            (sel.task_id != null && String(r.task_id) === String(sel.task_id)) ||
            (r.task_name === sel.task_name && Number(r.count) === Number(sel.count))
        );
      const otherSelected = (this.innerSelected || []).filter((sel) => !isOnCurrentPage(sel));
      const thisPageSelected = (rows || []).map((r) => ({
        task_id: r.task_id,
        id: r.id,
        task_name: r.task_name,
        count: r.count != null ? r.count : 0,
        work_location: r.work_location || "",
        punch_range_m: r.punch_range_m != null ? r.punch_range_m : 500,
      }));
      this.innerSelected = [...otherSelected, ...thisPageSelected];
    },
    syncTableSelection() {
      const table = this.$refs.taskTable;
      if (!table) return;
      table.clearSelection();
      this.syncingTable = true;
      const rows = this.loadedTaskList || [];
      this.innerSelected.forEach((sel) => {
        const row = rows.find(
          (r) =>
            (sel.task_id != null && String(r.task_id) === String(sel.task_id)) ||
            (r.task_name === sel.task_name && Number(r.count) === Number(sel.count))
        );
        if (row) table.toggleRowSelection(row, true);
      });
      this.$nextTick(() => {
        this.syncingTable = false;
      });
    },
    handleConfirm() {
      const out = this.innerSelected.map((t) => ({
        id: t.task_id != null ? t.task_id : t.id,
        task_id: t.task_id != null ? t.task_id : t.id,
        task_name: t.task_name || "",
        count: t.count != null ? t.count : 0,
        work_location: t.work_location || "",
        punch_range_m: t.punch_range_m != null ? t.punch_range_m : 500,
      }));
      this.$emit("input", out);
      this.dialogVisible = false;
    },
    onClose() {
      this.searchKeyword = "";
      this.syncingTable = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.dialog-body {
  background: #f5f7fa;
  padding: 16px;
  border-radius: 4px;
}
.search-row {
  display: flex;
  align-items: center;
  margin-bottom: 12px;
  .label {
    margin-right: 8px;
    color: #606266;
    white-space: nowrap;
  }
  .search-input {
    width: 200px;
    margin-right: 8px;
  }
}
.selected-row {
  margin-bottom: 12px;
  .label {
    margin-right: 8px;
    color: #606266;
  }
  .selected-tags {
    display: inline-flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    vertical-align: middle;
  }
  .selected-tag {
    margin: 0;
  }
  .placeholder {
    color: #909399;
    font-size: 12px;
  }
}
.task-table {
  background: #fff;
}
.pagination-row {
  margin-top: 12px;
  text-align: right;
}
</style>
